import React, {useEffect, useRef, useState} from 'react';
import NavPanel from "../components/NavPanel";
import BackButton from "../components/BackButton";
import '../styles/Admin.css';
import '../styles/AdminPayees.css';
import {URLS} from "../utils/urls";
import {pdfGetDefaultAPI, pdfPreviewAPI, pdfSettingsUpdateAPI} from "../http/pdfAPI";


type PromoRow = { left: string, right: string };
type ProductLabels = {
    idx: string, name: string, amount: string, priceNoVat: string,
    sumNoVat: string, totalNoVat: string, vatSum: string, totalWithVat: string,
};
type Layout = {
    pageHeightPx: number, pageMarginPx: number, minFreeSpacePx: number, advOffsetPx: number,
};
type Manager = { name: string, phone: string };
type ContentSettings = {
    site: string,
    manager: Manager[],
    siteLabel: string,
    warningText: string,
    currency: string,
    taglines: string[],
    promo: {
        title: string, subtitle: string, headLeft: string, headRight: string, rows: PromoRow[],
    },
    productLabels: ProductLabels,
    layout: Layout,
};

// How many "ответственный за счёт" rows the editor shows (matches the old
// PdfEditor manager form).
const MANAGER_SLOTS = 3;

const DEFAULTS: ContentSettings = {
    site: '',
    manager: [],
    siteLabel: 'Сайт - ',
    warningText: '',
    currency: 'грн',
    taglines: [],
    promo: { title: '', subtitle: '', headLeft: '', headRight: '', rows: [] },
    productLabels: {
        idx: '№', name: 'Товари (роботи, послуги)', amount: 'Кількість',
        priceNoVat: 'Ціна без ПДВ', sumNoVat: 'Сума без ПДВ',
        totalNoVat: 'Разом:', vatSum: 'Cума ПДВ:', totalWithVat: 'Усього з ПДВ:',
    },
    layout: { pageHeightPx: 1219, pageMarginPx: 20, minFreeSpacePx: 50, advOffsetPx: 20 },
};

const PRODUCT_LABEL_FIELDS: Array<{ key: keyof ProductLabels, label: string }> = [
    { key: 'idx', label: 'Колонка «№»' },
    { key: 'name', label: 'Колонка «наименование»' },
    { key: 'amount', label: 'Колонка «количество»' },
    { key: 'priceNoVat', label: 'Колонка «цена»' },
    { key: 'sumNoVat', label: 'Колонка «сумма»' },
    { key: 'totalNoVat', label: 'Итог «Разом»' },
    { key: 'vatSum', label: 'Итог «Сума ПДВ»' },
    { key: 'totalWithVat', label: 'Итог «Усього з ПДВ»' },
];

const LAYOUT_FIELDS: Array<{ key: keyof Layout, label: string }> = [
    { key: 'pageHeightPx', label: 'Высота страницы, px' },
    { key: 'pageMarginPx', label: 'Поле страницы, px' },
    { key: 'minFreeSpacePx', label: 'Мин. свободное место, px' },
    { key: 'advOffsetPx', label: 'Отступ рекламного блока, px' },
];

const merge = (raw: any): ContentSettings => ({
    ...DEFAULTS,
    ...(raw || {}),
    site: typeof raw?.site === 'string' ? raw.site : DEFAULTS.site,
    manager: Array.isArray(raw?.manager)
        ? raw.manager.map((m: any) => ({ name: `${m?.name ?? ''}`, phone: `${m?.phone ?? ''}` }))
        : DEFAULTS.manager,
    taglines: Array.isArray(raw?.taglines) ? raw.taglines : DEFAULTS.taglines,
    promo: {
        ...DEFAULTS.promo,
        ...(raw?.promo || {}),
        rows: Array.isArray(raw?.promo?.rows) ? raw.promo.rows : DEFAULTS.promo.rows,
    },
    productLabels: { ...DEFAULTS.productLabels, ...(raw?.productLabels || {}) },
    layout: { ...DEFAULTS.layout, ...(raw?.layout || {}) },
});


const AdminPdfContent = () => {
    const [settings, setSettings] = useState<ContentSettings | null>(null);
    const [msg, setMsg] = useState<{ text: string, type: 'error' | 'success' } | null>(null);

    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [previewLoading, setPreviewLoading] = useState<boolean>(false);
    const previewReq = useRef<number>(0);

    useEffect(() => {
        pdfGetDefaultAPI().then((res) => {
            if (res?.data?.pdfSettings) setSettings(merge(res.data.pdfSettings));
        });
    }, []);

    // Debounced live preview: re-render the invoice HTML ~500ms after edits stop.
    useEffect(() => {
        if (!settings) return;
        const reqId = ++previewReq.current;
        setPreviewLoading(true);
        const t = setTimeout(() => {
            pdfPreviewAPI({ pdfSettings: settings }).then((res) => {
                if (reqId !== previewReq.current) return; // a newer edit superseded this
                if (typeof res?.data?.html === 'string') setPreviewHtml(res.data.html);
                setPreviewLoading(false);
            });
        }, 500);
        return () => clearTimeout(t);
    }, [settings]);

    const flash = (text: string, type: 'error' | 'success') => {
        setMsg({ text, type });
        setTimeout(() => setMsg(null), 4000);
    };

    if (!settings) {
        return (
            <div className='admin page-content-container'>
                <NavPanel/>
                <div className='admin__body page_content_body'>
                    <div className='contentWindow' style={{ padding: 'var(--space-4)' }}>Загрузка…</div>
                </div>
            </div>
        );
    }

    const patch = (part: Partial<ContentSettings>) => setSettings({ ...settings, ...part });

    // Read one "ответственный за счёт" slot (name/phone) — the stored array may
    // be shorter than MANAGER_SLOTS.
    const managerAt = (i: number): Manager => settings.manager[i] || { name: '', phone: '' };
    const patchManager = (i: number, part: Partial<Manager>) => {
        const manager = Array.from({ length: MANAGER_SLOTS }, (_, j) => ({ ...managerAt(j) }));
        manager[i] = { ...manager[i], ...part };
        patch({ manager });
    };

    const save = async () => {
        // Drop fully-empty responsible rows before storing.
        const payload = {
            ...settings,
            manager: settings.manager.filter((m) => m.name.trim() !== '' || m.phone.trim() !== ''),
        };
        const res = await pdfSettingsUpdateAPI(payload);
        if (res?.response?.status >= 400) {
            flash(res?.response?.data?.message || 'Не удалось сохранить', 'error');
            return;
        }
        flash('Сохранено', 'success');
    };

    return (
        <div className='admin page-content-container'>
            <NavPanel/>
            <div className='admin__body page_content_body'>
                <div className="admin__content-container contentWindow payee-page" style={{ width: '100%' }}>
                    <div className="admin__data-header contentWindow__header">
                        <BackButton to={URLS.ADMIN_URL}/>
                        <span className='contentWindow__name'>Содержимое счёта</span>
                    </div>
                    <nav className="admin__data-nav contentWindow__nav payee__toolbar">
                        <button className="nav__button btn-primary" onClick={save}>Сохранить</button>
                        {msg && (
                            <span className='payee__msg'
                                  style={{ color: msg.type === 'error' ? 'var(--color-danger)' : 'var(--color-success)' }}>
                                {msg.text}
                            </span>
                        )}
                    </nav>

                    <div className="contentWindow__body payee__body">
                      <div className="pdfc__layout">
                        <div className="pdfc__sections">

                        <section className='payee__section'>
                            <h3 className='payee__section-title'>Подпись счёта</h3>
                            <label className='payee__label'>
                                <span>Адрес сайта (URL) в подписи счёта</span>
                                <input className='input' value={settings.site}
                                       placeholder='например, www.example.com'
                                       onChange={(e) => patch({ site: e.target.value })}/>
                            </label>
                            <div className='payee__label'>
                                <span>Ответственные за счёт (имя и телефон)</span>
                                <span className='payee__field-hint'>
                                    Показываются в блоке «Відповідальний за рахунок». Пустые строки не сохраняются.
                                </span>
                            </div>
                            {Array.from({ length: MANAGER_SLOTS }, (_, i) => (
                                <div key={i} className='payee__row-2'>
                                    <input className='input' placeholder='Имя'
                                           value={managerAt(i).name}
                                           onChange={(e) => patchManager(i, { name: e.target.value })}/>
                                    <input className='input' placeholder='Телефон'
                                           value={managerAt(i).phone}
                                           onChange={(e) => patchManager(i, { phone: e.target.value })}/>
                                </div>
                            ))}
                        </section>

                        <section className='payee__section'>
                            <h3 className='payee__section-title'>Шапка</h3>
                            <label className='payee__label'>
                                <span>Текст-предупреждение (первая строка счёта)</span>
                                <textarea className='input' rows={3}
                                          value={settings.warningText}
                                          onChange={(e) => patch({ warningText: e.target.value })}/>
                            </label>
                            <div className='payee__row-2'>
                                <label className='payee__label'>
                                    <span>Подпись перед адресом сайта</span>
                                    <input className='input' value={settings.siteLabel}
                                           onChange={(e) => patch({ siteLabel: e.target.value })}/>
                                </label>
                                <label className='payee__label'>
                                    <span>Валюта</span>
                                    <input className='input' value={settings.currency}
                                           onChange={(e) => patch({ currency: e.target.value })}/>
                                </label>
                            </div>
                        </section>

                        <section className='payee__section'>
                            <h3 className='payee__section-title'>Слоганы (под логотипом)</h3>
                            {settings.taglines.map((line, i) => (
                                <div key={i} className='payee__list-row'>
                                    <input className='input' value={line}
                                           onChange={(e) => {
                                               const taglines = [...settings.taglines];
                                               taglines[i] = e.target.value;
                                               patch({ taglines });
                                           }}/>
                                    <button className='nav__button btn-ghost'
                                            onClick={() => patch({ taglines: settings.taglines.filter((_, j) => j !== i) })}>
                                        ✕
                                    </button>
                                </div>
                            ))}
                            <button className='nav__button btn-ghost'
                                    onClick={() => patch({ taglines: [...settings.taglines, ''] })}>
                                + Добавить строку
                            </button>
                        </section>

                        <section className='payee__section'>
                            <h3 className='payee__section-title'>Рекламный блок</h3>
                            <div className='payee__row-2'>
                                <label className='payee__label'>
                                    <span>Заголовок</span>
                                    <input className='input' value={settings.promo.title}
                                           onChange={(e) => patch({ promo: { ...settings.promo, title: e.target.value } })}/>
                                </label>
                                <label className='payee__label'>
                                    <span>Подзаголовок</span>
                                    <input className='input' value={settings.promo.subtitle}
                                           onChange={(e) => patch({ promo: { ...settings.promo, subtitle: e.target.value } })}/>
                                </label>
                            </div>
                            <div className='payee__row-2'>
                                <label className='payee__label'>
                                    <span>Шапка левой колонки</span>
                                    <input className='input' value={settings.promo.headLeft}
                                           onChange={(e) => patch({ promo: { ...settings.promo, headLeft: e.target.value } })}/>
                                </label>
                                <label className='payee__label'>
                                    <span>Шапка правой колонки</span>
                                    <input className='input' value={settings.promo.headRight}
                                           onChange={(e) => patch({ promo: { ...settings.promo, headRight: e.target.value } })}/>
                                </label>
                            </div>
                            {settings.promo.rows.map((row, i) => (
                                <div key={i} className='payee__list-row'>
                                    <input className='input' placeholder='слева' value={row.left}
                                           onChange={(e) => {
                                               const rows = settings.promo.rows.map((r, j) => j === i ? { ...r, left: e.target.value } : r);
                                               patch({ promo: { ...settings.promo, rows } });
                                           }}/>
                                    <input className='input' placeholder='справа' value={row.right}
                                           onChange={(e) => {
                                               const rows = settings.promo.rows.map((r, j) => j === i ? { ...r, right: e.target.value } : r);
                                               patch({ promo: { ...settings.promo, rows } });
                                           }}/>
                                    <button className='nav__button btn-ghost'
                                            onClick={() => patch({ promo: { ...settings.promo, rows: settings.promo.rows.filter((_, j) => j !== i) } })}>
                                        ✕
                                    </button>
                                </div>
                            ))}
                            <button className='nav__button btn-ghost'
                                    onClick={() => patch({ promo: { ...settings.promo, rows: [...settings.promo.rows, { left: '', right: '' }] } })}>
                                + Добавить строку каталога
                            </button>
                        </section>

                        <section className='payee__section'>
                            <h3 className='payee__section-title'>Подписи таблицы товаров</h3>
                            <div className='payee__row-2'>
                                {PRODUCT_LABEL_FIELDS.map(({ key, label }) => (
                                    <label key={key} className='payee__label'>
                                        <span>{label}</span>
                                        <input className='input' value={settings.productLabels[key]}
                                               onChange={(e) => patch({ productLabels: { ...settings.productLabels, [key]: e.target.value } })}/>
                                    </label>
                                ))}
                            </div>
                        </section>

                        <section className='payee__section'>
                            <h3 className='payee__section-title'>Разметка страницы (px)</h3>
                            <p className='payee__hint'>
                                Константы для скрипта, прижимающего рекламный блок к низу первой страницы. Менять только при необходимости.
                            </p>
                            <div className='payee__row-2'>
                                {LAYOUT_FIELDS.map(({ key, label }) => (
                                    <label key={key} className='payee__label'>
                                        <span>{label}</span>
                                        <input className='input' type='number' value={settings.layout[key]}
                                               onChange={(e) => patch({ layout: { ...settings.layout, [key]: Number(e.target.value) } })}/>
                                    </label>
                                ))}
                            </div>
                        </section>

                        </div>

                        <aside className="pdfc__preview">
                            <div className="pdfc__preview-head">
                                <span>Предпросмотр счёта</span>
                                {previewLoading && <span className="pdfc__preview-loading">обновление…</span>}
                            </div>
                            <div className="pdfc__preview-frame">
                                {previewHtml
                                    ? <iframe title="Предпросмотр счёта" srcDoc={previewHtml} sandbox="allow-scripts"/>
                                    : <div className="pdfc__preview-empty">—</div>}
                            </div>
                            <p className="payee__hint">
                                Пример с типовыми позициями и получателем по умолчанию. Разбивка на страницы в готовом PDF может немного отличаться.
                            </p>
                        </aside>
                      </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPdfContent;
